import useFavorite from "features/leads/datahooks/useFavorite";

import { Button } from "common/components/ui/Button";
import { DialogFooter, DialogHeader } from "common/components/ui/Dialog";
import { DrawerFooter, DrawerHeader } from "common/components/ui/Drawer";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type { Lead } from "common/types";

import { startLeadsBulkAction } from "../../../utils";

interface FavoriteLeadsProps {
  action: "favorite" | "unfavorite";
  selectedLeads: Lead[];
  onBack: () => void;
  onSuccess: () => void;
}

export default function FavoriteLeads({
  action,
  selectedLeads,
  onBack,
  onSuccess,
}: FavoriteLeadsProps) {
  const isDesktop = useTwBreakpoint("lg");

  const Footer = isDesktop ? DialogFooter : DrawerFooter;

  const Header = isDesktop ? DialogHeader : DrawerHeader;
  const { addFavorite, removeFavorite } = useFavorite();

  let title = "Favorite leads";
  let description =
    selectedLeads.length === 1
      ? `Add ${selectedLeads[0].full_name} to your favorites?`
      : `Add ${selectedLeads.length} leads to your favorites?`;

  if (action === "unfavorite") {
    title = "Unfavorite leads";
    description =
      selectedLeads.length === 1
        ? `Remove ${selectedLeads[0].full_name} from your favorites?`
        : `Remove ${selectedLeads.length} leads from your favorites?`;
  }

  function onFavorite() {
    startLeadsBulkAction(
      (lead, abortSignal) => {
        const actionFunction =
          action === "favorite" ? addFavorite : removeFavorite;
        return actionFunction({
          leadId: lead.id,
          abortSignal,
          updateCacheOn: "success",
        });
      },
      selectedLeads,
      (finishedItemsCount) =>
        finishedItemsCount === selectedLeads.length
          ? `Leads ${action === "favorite" ? "favorited" : "unfavorited"}`
          : `${action === "favorite" ? "Favorited" : "Unfavorited"} ${finishedItemsCount}/${selectedLeads.length} leads`,
      true,
    );
    onSuccess();
  }

  return (
    <>
      <Header title={title} subtitle={description} onBack={onBack} />

      <Footer className="justify-end">
        <Button variant="secondary-black" onClick={onBack}>
          Back
        </Button>

        <Button
          onClick={onFavorite}
          className="capitalize"
          disabled={!selectedLeads.length}
        >
          {action}
        </Button>
      </Footer>
    </>
  );
}
